/**
 * Single source of truth for all colors used in the app.
 *
 * Design language: minimalist + modern + friendly.
 *   • Neutrals: slate (cool grays).
 *   • Accent: sky/blue family (trustworthy, calm).
 *   • Surfaces: near-white cards on a faintly tinted background.
 *   • Gradients: only used as ambient backdrops (very subtle); buttons are solid.
 *
 * If you need a new color, add it here. Do NOT hardcode hex values in components.
 */
export var palette = {
    sky: {
        50: '#f0f9ff',
        100: '#e0f2fe',
        200: '#bae6fd',
        300: '#7dd3fc',
        400: '#38bdf8',
        500: '#0ea5e9',
        600: '#0284c7',
        700: '#0369a1',
        800: '#075985',
        900: '#0c4a6e',
    },
    slate: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
    },
    blue: {
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
    },
    white: '#ffffff',
};
export var brand = {
    /**
     * Confident sky-600 as the main action color (a bit darker than sky-500
     * for better contrast on white surfaces and accessibility).
     */
    primary: {
        main: palette.sky[600],
        light: palette.sky[500],
        dark: palette.sky[700],
        contrastText: palette.white,
    },
    secondary: {
        main: palette.slate[600],
        light: palette.slate[400],
        dark: palette.slate[700],
        contrastText: palette.white,
    },
    background: {
        /** Very faint slate so cards stand out without harsh white-on-white. */
        default: palette.slate[50],
        paper: palette.white,
        /** Subtle accent tint used as the auth-screen backdrop. */
        surfaceMuted: '#f5f8fc',
    },
    border: palette.slate[200],
    text: {
        primary: palette.slate[900],
        secondary: palette.slate[500],
        muted: palette.slate[400],
    },
    scrollbar: {
        track: palette.slate[100],
        thumb: palette.slate[300],
        thumbHover: palette.slate[400],
    },
};
export var gradients = {
    /** Soft sky tint for the login screen — no more purple. */
    loginBackdrop: "linear-gradient(180deg, ".concat(palette.sky[50], " 0%, ").concat(palette.slate[50], " 100%)"),
    /** Buttons stay solid for a cleaner, more modern feel. */
    primaryButton: palette.sky[600],
    primaryButtonHover: palette.sky[700],
};
/**
 * Restriction badge colors used in the availability calendar and the
 * restrictions list. Keys must match the backend restriction `code`.
 *
 * Tones tuned to be readable on white and consistent with the sky/slate base.
 */
export var restriction = {
    CLOSED_TO_ARRIVAL: '#dc2626', // red-600
    CLOSED_TO_DEPARTURE: '#ea580c', // orange-600
    MIN_STAY_ARRIVAL: '#2563eb', // blue-600
    MIN_STAY_THROUGH: '#4f46e5', // indigo-600
    MAX_STAY: '#7c3aed', // violet-600
    STOP_SELL: '#e11d48', // rose-600
    SS: '#e11d48',
    AVAILABILITY_OFFSET: '#0891b2', // cyan-600
    AVAILABILITY_PER_RATE: '#0e7490', // cyan-700
    MAX_AVAILABILITY: '#16a34a', // green-600
    ONLY_AVAILABILITY: '#15803d', // green-700
    RATE_AND_AVAILABILITY: '#65a30d', // lime-600
};
